// -------------------------------------------------------------------------------------------------
// NortonLifeLock copyright header start
// ----------------------
//
// PROPRIETARY / CONFIDENTIAL.
// Use of this product is subject to license terms.
// Copyright (c) 2022 NortonLifeLock
// All rights reserved.
//
// -------------------------------------------------------------------------------------------------
// NortonLifeLock copyright header stop
// ----------------------
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend, { BackendOptions, RequestCallback } from 'i18next-http-backend';

const loadPath = '/locales/{{lng}}/{{ns}}.json';

const addQueryString = (url: string, params: {} | string | any) => {
  if (params && typeof params === 'object') {
    let queryString = '';

    for (const paramName in params) {
      queryString +=
        '&' + encodeURIComponent(paramName) + '=' + encodeURIComponent(params[paramName]);
    }

    if (!queryString) {
      return url;
    }
    url = url + (url.includes('?') ? '&' : '?') + queryString.slice(1);
  }

  return url;
};

const request = (
  options: BackendOptions,
  url: string,
  payload: {} | string,
  callback: RequestCallback,
) => {
  if (payload && typeof payload === 'object') {
    payload = addQueryString('', payload).slice(1);
  }

  if (options.queryStringParams) {
    url = addQueryString(url, options.queryStringParams);
  }

  try {
    const xhr = new window.XMLHttpRequest();

    const asynchronously = true;
    xhr.open(payload ? 'POST' : 'GET', url, asynchronously);

    if (!options.crossDomain) {
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    }

    xhr.withCredentials = !!options.withCredentials;

    if (payload) {
      xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
    }

    if (xhr.overrideMimeType) {
      xhr.overrideMimeType('application/json');
    }

    let headers = options.customHeaders;

    headers = typeof headers === 'function' ? headers() : headers;

    if (headers) {
      for (const index in headers) {
        xhr.setRequestHeader(index, headers[index]);
      }
    }

    const BAD_REQUEST = 400;
    xhr.onreadystatechange = () => {
      xhr.readyState > 3 &&
        callback(xhr.status >= BAD_REQUEST ? xhr.statusText : null, {
          status: xhr.status,
          data: xhr.responseText,
        });
    };

    xhr.send(payload as Document);
  } catch (e) {
    global.console.error(e);
  }
};

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    detection: {
      order: ['navigator'],
    },
    backend: {
      loadPath,
      allowMultiLoading: false,
      crossDomain: false,
      request, // 👈 Custom XHR request due to fetch API unable to work with "res://" scheme.
    },
    compatibilityJSON: 'v3',
    initImmediate: false,
    debug: true,
    fallbackLng: 'en',
    defaultNS: 'translation',
    load: 'all', // 👈 strategy to define which language codes to lookup. Example: given set language is en-US: - 'all'  ⇒ ['en-US', 'en', 'dev'] - 'currentOnly'  ⇒ 'en-US' - 'languageOnly'  ⇒ 'en'
    keySeparator: '>',
    nsSeparator: ':',
    interpolation: {
      escapeValue: false, // not needed for react!!
      formatSeparator: ',',
    },
    react: {
      useSuspense: true,
      transSupportBasicHtmlNodes: true, // allow <br/> and simple html elements in translation
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i'], // don't convert to <1></1> if simple react elements
    },
  });

export default i18n;
